import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoryOpen, setMobileCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setMobileCategoryOpen(false);
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNav = (path: string) => {
    setMobileOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileOpen
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a
              href="/"
              className="font-heading text-xl md:text-2xl font-extrabold text-foreground-800 tracking-tight hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                handleNav('/');
              }}
            >
              HipobuySpread
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-3 md:gap-5">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 bg-background-100 hover:bg-accent-100 hover:text-accent-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Categories
                  {dropdownOpen ? (
                    <i className="ri-arrow-up-s-line text-base w-4 h-4 flex items-center justify-center"></i>
                  ) : (
                    <i className="ri-arrow-down-s-line text-base w-4 h-4 flex items-center justify-center"></i>
                  )}
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-lg border border-background-200/60 shadow-lg py-2 max-h-[60vh] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                      >
                        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                          <i className={`${cat.icon} text-sm`}></i>
                        </span>
                        {cat.displayName}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => handleNav('/hipobuy-spreadsheet-guide')}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Guides
              </button>

              <button
                onClick={() => handleNav('/hipobuy-vs-kakobuy')}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Comparisons
              </button>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                hipobuy sheet
              </a>
            </div>

            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-all duration-200 cursor-pointer"
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-xl"></i>
              ) : (
                <i className="ri-menu-line text-xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          ></div>

          {/* Menu Panel */}
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto">
            <div className="px-6 pt-6 pb-10">
              {/* Quick Actions */}
              <div className="flex flex-col gap-3 mb-8">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  hipobuy sheet
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold border-2 border-foreground-800 text-foreground-800 hover:bg-foreground-800 hover:text-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
                </a>
              </div>

              {/* Navigation Links */}
              <div className="border-t border-background-200/60 pt-6 mb-6">
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4">Navigate</h3>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => handleNav('/')}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors duration-150 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-home-line text-sm"></i>
                    </span>
                    Home
                  </button>
                  <button
                    onClick={() => handleNav('/hipobuy-spreadsheet-guide')}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors duration-150 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-book-open-line text-sm"></i>
                    </span>
                    Buying Guides
                  </button>
                  <button
                    onClick={() => handleNav('/hipobuy-vs-kakobuy')}
                    className="flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors duration-150 cursor-pointer"
                  >
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-200 text-secondary-800">
                      <i className="ri-scales-line text-sm"></i>
                    </span>
                    Comparisons
                  </button>
                </div>
              </div>

              {/* Categories Section */}
              <div className="border-t border-background-200/60 pt-6">
                <button
                  onClick={() => setMobileCategoryOpen(!mobileCategoryOpen)}
                  className="w-full flex items-center justify-between mb-4 cursor-pointer"
                >
                  <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                    Categories ({categories.length})
                  </h3>
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 transition-transform duration-200 ${mobileCategoryOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-sm"></i>
                  </span>
                </button>

                {mobileCategoryOpen && (
                  <div className="grid grid-cols-2 gap-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2.5 p-3 rounded-lg bg-background-100 border border-background-200/60 hover:bg-accent-50 hover:border-accent-200 transition-all duration-150 cursor-pointer"
                      >
                        <span className="w-7 h-7 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                          <i className={`${cat.icon} text-xs`}></i>
                        </span>
                        <span className="text-xs font-medium text-foreground-700">{cat.displayName}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer Brand */}
              <div className="mt-10 pt-6 border-t border-background-200/60 text-center">
                <p className="font-heading text-lg font-extrabold text-foreground-800">HipobuySpread</p>
                <p className="text-xs text-foreground-400 mt-1">Curated product spreadsheet finds</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}